import { useEffect, useMemo, useState } from 'react';
import { useRequiredParams } from 'app/router/params';

import Navbar from 'app/components/editor/navbar/Navbar';
import Sidebar from 'app/components/editor/sidebar/Sidebar';
import { Editor, DecoratorFactory } from 'app/components/editor/Editor';
import withMentions from 'slate/extensions/mentions';
import withImportArea from 'slate/extensions/import-area';
import { SidebarContextProvider } from 'app/state/contexts/SidebarContext';
import { TagContextProvider } from 'app/state/contexts/TagContext';
import withNotes from 'slate/extensions/notes';
import withPage from 'slate/extensions/page';
import withParagraph from 'slate/extensions/paragraph';
import withLayout from 'slate/extensions/layout';
import withImage from 'slate/extensions/image';
import withStyledParagraph from 'slate/extensions/styled-paragraph';
import { generateDiffState } from 'slate/compareUtils';
import { DiffContext, DiffState } from 'app/state/contexts/DiffContext';
import VersionSelector from './VersionSelector';
import { SupportModal } from 'app/components';
import withReferenceTable from 'slate/extensions/reference-table';
import { Spinner } from 'app/components';
import withTableOfContent from 'slate/extensions/table-of-content';
import { v4 as uuidv4 } from 'uuid';
import { useRevisionL, useRevisionR } from 'app/components/editor/sidebar/useRevision';
import { withDecorateCompare } from 'slate/decorators/compareDecorator';
import withSideBySide from 'slate/extensions/sidebyside';
import { useDocumentRevision, useSectionRevision, useSimpleVersions, SectionEntry, type EditorDocument } from 'entities/document';
import { useReferenceList } from 'entities/reference-data';

interface DocumentRevision {
  revisionId?: string;
  content?: { sections?: SectionEntry[] };
}

type SectionRevision = {
  revisionId?: string;
  content?: unknown[];
  name?: string;
};

const defaultDiffState: DiffState = { left: {}, right: {}, leftSource: {}, rightSource: {} };

const extensionsL = [
  withLayout(),
  withSideBySide({ side: 'left', readOnly: true }),
  withMentions,
  withImportArea({ readOnly: true }),
  withImage({ readOnly: true }),
  withPage({ withButtons: false, readOnly: true }),
  withNotes,
  withParagraph({ withComments: false }),
  withReferenceTable({ readOnly: true }),
  withTableOfContent({ readOnly: true }),
  withStyledParagraph({ isPDF: false }),
];

const extensionsR = [
  withLayout({ markEmpty: true }),
  withSideBySide({ side: 'right', readOnly: true }),
  withMentions,
  withImportArea({ readOnly: true }),
  withImage({ readOnly: true }),
  withPage({ withButtons: true, readOnly: true }),
  withNotes,
  withParagraph({ withComments: true }),
  withReferenceTable({ readOnly: true }),
  withTableOfContent({ readOnly: true }),
  withStyledParagraph({ isPDF: false }),
];

const revisionIdOrCurrent = (revisionId: string | null) => {
  return revisionId === 'current-draft' ? '' : revisionId;
};

const findSectionRevisionId = (document: DocumentRevision | null | undefined, sectionSuperId: string) => {
  return document?.content?.sections?.find((section: SectionEntry) => section.superId === sectionSuperId)?.revisionId;
};

const SideBySideViewer = () => {
  const { documentId, sectionId } = useRequiredParams('documentId', 'sectionId');
  const [revisionIdLeft, setRevisionIdLeft] = useRevisionL();
  const [revisionIdRight, setRevisionIdRight] = useRevisionR();

  const [diffState, setDiffState] = useState<DiffState>(defaultDiffState);

  const handleChangeVersionL = (value: string | null) => {
    setDiffState(defaultDiffState);
    setRevisionIdLeft(revisionIdOrCurrent(value));
  };

  const handleChangeVersionR = (value: string | null) => {
    setDiffState(defaultDiffState);
    setRevisionIdRight(revisionIdOrCurrent(value));
  };
  const decoratorR = useMemo(() => [withDecorateCompare({ side: 'right', diffState })], [diffState]);
  const decoratorL = useMemo(() => [withDecorateCompare({ side: 'left', diffState })], [diffState]);

  // Fetch versions
  const { data: versions = [] } = useSimpleVersions(documentId);

  // Fetch left side: document → derive section revisionId → fetch section
  const { data: documentL = {}, isLoading: docLoadingL }: { data?: DocumentRevision; isLoading: boolean } = useDocumentRevision(
    revisionIdLeft || undefined,
    revisionIdLeft ? undefined : documentId
  );
  const sectionRevisionIdL = findSectionRevisionId(documentL, sectionId);
  const { data: sectionL = {}, isLoading: secLoadingL }: { data?: SectionRevision; isLoading: boolean } = useSectionRevision(sectionRevisionIdL ?? '');
  const loadingL = docLoadingL || secLoadingL;

  // Fetch right side: document → derive section revisionId → fetch section
  const { data: documentR = {}, isLoading: docLoadingR }: { data?: DocumentRevision; isLoading: boolean } = useDocumentRevision(
    revisionIdRight || undefined,
    revisionIdRight ? undefined : documentId
  );
  const sectionRevisionIdR = findSectionRevisionId(documentR, sectionId);
  const { data: referencesL }: { data?: unknown[] } = useReferenceList((documentL?.revisionId ? documentL : undefined) as EditorDocument | undefined);
  const { data: referencesR }: { data?: unknown[] } = useReferenceList((documentR?.revisionId ? documentR : undefined) as EditorDocument | undefined);
  const { data: sectionR = {}, isLoading: secLoadingR }: { data?: SectionRevision; isLoading: boolean } = useSectionRevision(sectionRevisionIdR ?? '');
  const loadingR = docLoadingR || secLoadingR;

  useEffect(() => {
    const contentL = sectionL.content?.length ? sectionL.content : fallback;
    const contentR = sectionR.content?.length ? sectionR.content : fallback;
    setDiffState(generateDiffState({ content: contentL, references: referencesL }, { content: contentR, references: referencesR }));
  }, [sectionL.content, sectionR.content, referencesL, referencesR]);

  const instanceL = { documentId, documentRevisionId: documentL.revisionId, sectionId, sectionRevisionId: sectionL.revisionId };
  const instanceR = { documentId, documentRevisionId: documentR.revisionId, sectionId, sectionRevisionId: sectionR.revisionId };

  return (
    <SidebarContextProvider>
      <TagContextProvider>
        <DiffContext.Provider value={{ diffState, setDiffState }}>
          <div className="side-by-side mx-3">
            <div className="my-3">
              <div className="top"></div>
              {loadingL ? (
                <div>
                  <Spinner />
                </div>
              ) : (
                <Editor
                  key={sectionL.revisionId}
                  showDiff
                  readOnly={true}
                  extensions={extensionsL}
                  instance={instanceL}
                  decorators={decoratorL as DecoratorFactory[]}
                >
                  <div className="section-name fw-bold">{sectionL.name ? sectionL.name : ''}</div>
                </Editor>
              )}
            </div>
            <div className="my-3">
              <div className="top"></div>
              {loadingR ? (
                <div>
                  <Spinner />
                </div>
              ) : (
                <Editor
                  key={sectionR.revisionId}
                  showDiff
                  readOnly={true}
                  extensions={extensionsR}
                  instance={instanceR}
                  decorators={decoratorR as DecoratorFactory[]}
                >
                  <div className="section-name fw-bold">{sectionR.name ? sectionR.name : ''}</div>
                  <Navbar showXliff={false} prevLink={`/documents/${documentId}/sections/${sectionId}`} />
                  <VersionSelector
                    versions={versions}
                    documentId={documentId}
                    handleChangeVersionL={handleChangeVersionL}
                    revisionIdLeft={revisionIdLeft}
                    documentL={documentL}
                    handleChangeVersionR={handleChangeVersionR}
                    revisionIdRight={revisionIdRight}
                    documentR={documentR}
                  />
                  <Sidebar isSideBySide={true} setRevisionL={handleChangeVersionL} />
                  <SupportModal />
                </Editor>
              )}
            </div>
          </div>
        </DiffContext.Provider>
      </TagContextProvider>
    </SidebarContextProvider>
  );
};

const fallback = [
  {
    type: 'page',
    uuid: uuidv4(),
    children: [
      {
        type: 'layout',
        children: [
          {
            type: 'paragraph',
            children: [
              {
                text: 'Motsvarande sektion saknas i den här versionen',
              },
            ],
          },
        ],
      },
    ],
  },
];

export default SideBySideViewer;
